import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SuntecLogo from "../assets/logo1.png";
import theme from "../theme";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { APIConstants } from "../constants/ApiConstants";
import Loader from "../components/ui/Loader";
import useButtonStyles from "../styles/useButtonStyles";
import { useReactToPrint } from "react-to-print";

export interface EvoucherSalesReport {
  data: any;
  title: string;
  heading: string;
  type?: "eod" | "summary";
}

const EvoucherSalesReport: React.FC<EvoucherSalesReport> = (
  props: EvoucherSalesReport
) => {
  const { data, title, heading, type } = props;
  const currentRef = useRef(null);
  const buttonStyles = useButtonStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [rawData, setRawData] = useState<any>([]);
  const [formattedData, setFormattedData] = useState<any>();
  const [totalQty, setTotalQty] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [paymentTypesTotal, setPaymentTypesTotal] = useState<any[]>([]);
  const reportCalculations = (transactionList: any) => {
    const paymentTypes: any = Array.from(
      new Set(transactionList?.map((voucher: any) => voucher?.paymentType))
    );
    const paymentTypeTotalAmount: any = paymentTypes.reduce(
      (list: any, paymentType: any) => {
        const obj: any = {};
        const filteredVouchers = transactionList?.filter(
          (voucher: any) => voucher?.paymentType === paymentType
        );
        const total = filteredVouchers.reduce((sum: number, item: any) => {
          return (sum = sum + +item?.amount);
        }, 0);
        obj["type"] = paymentType;
        obj["total"] = total;
        return [...list, obj];
      },
      []
    );
    setPaymentTypesTotal(paymentTypeTotalAmount);
    setLoading(false);
  };

  const getReportData = (rawData: any) => {
    const reportData = rawData
      .map((i: any) =>
        i.line_items?.line_item.map((x: any) => {
          x.paymentType = i?.custom_fields?.field.filter(
            (field: any) => field.name === "payment_mode"
          )[0]?.value;
          return x;
        })
      )
      .flat();
    reportCalculations(reportData);

    if (reportData) {
      const resultList = reportData.reduce((accumulator: any, item: any) => {
        const description = item?.description;
        const paymentType = item?.paymentType;
        const qty = parseFloat(item?.qty);
        const rate = parseFloat(item?.rate);
        const key = `${description}-${paymentType}`;
        if (accumulator[key]) {
          accumulator[key].qty += qty;
          accumulator[key].rate = rate;
          accumulator[key].amount =
            accumulator[key].qty * accumulator[key].rate;
        } else {
          accumulator[key] = {
            description: description,
            paymentType: paymentType,
            qty: qty,
            rate: rate,
            amount: qty * rate,
          };
        }
        return accumulator;
      }, {});

      const resultArray = Object.values(resultList);
      const totalAmount = resultArray.reduce((sum: number, item: any) => {
        return sum + (item?.amount as number);
      }, 0);
      const TotalQuantity = resultArray.reduce((sum: number, item: any) => {
        return sum + (item?.qty as number);
      }, 0);
      setTotalQty(TotalQuantity);
      setTotalAmount(totalAmount && totalAmount);
      setFormattedData(resultArray);
    }
  };

  const formatDate = (date: any) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, 0)}-${date.getDate().toString().padStart(2, 0)}`;
  };

  const fetchReports = async () => {
    setLoading(true);
    let url = process.env.REACT_APP_API_URL + APIConstants.salesReport;
    url = url + `?date=${formatDate(new Date())}`;
    const response = await axios.get(url);
    let responseData = response?.data?.data?.transaction;
    if (responseData?.length > 0) {
      if (type === "summary") {
        responseData = responseData?.filter(
          (report: any) =>
            report?.custom_fields?.field?.filter(
              (field: any) => field?.name === "cs_email_id"
            )[0]?.value === localStorage?.username
        );
        getReportData(responseData);
      }
      if (type === "eod") {
        getReportData(response?.data?.data?.transaction);
      }
      setRawData(response?.data?.data?.transaction);
    } else {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => {
      if (currentRef.current) return currentRef.current;
      return null;
    },
  });

  useEffect(() => {
    fetchReports();
  }, []);

  return (
    <Grid
      my={2}
      width={"60%"}
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      sx={{ backgroundColor: "white", p: 2, borderRadius: "6px" }}
    >
      <Typography
        variant="h6"
        my={2}
        sx={{
          color: theme?.color?.primary,
          fontWeight: 600,
          fontFamily: theme?.font?.fontFamily.primary,
        }}
      >
        {title}
      </Typography>
      {rawData?.length > 0 && paymentTypesTotal?.length >= 0 ? (
        <div
          ref={currentRef}
          style={{
            padding: "1rem",
          }}
        >
          <Grid
            width={"100%"}
            container
            border={"2px solid black"}
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
          >
            <Grid item container mb={2}>
              <Grid item xs={3}>
                <img
                  style={{
                    objectFit: "cover",
                    width: "6rem",
                    height: "6rem",
                    padding: "1rem",
                  }}
                  src={SuntecLogo}
                  alt=""
                />
              </Grid>
              <Grid
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                item
                xs={6}
                justifyContent={"center"}
              >
                <Typography sx={{ fontWeight: 600, my: 0.5 }}>
                  {heading}
                </Typography>
                <Typography
                  sx={{ fontFamily: theme.font?.fontFamily.primary, my: 0.5 }}
                >
                  Suntec City
                </Typography>
                <Typography
                  sx={{ fontFamily: theme.font?.fontFamily.primary, my: 0.5 }}
                >
                  {new Date().toDateString()}
                </Typography>
                <Typography sx={{ fontWeight: 600, my: 0.5 }}>
                  Sales of Voucher
                </Typography>
              </Grid>
              <Grid item xs={3}></Grid>
            </Grid>
            <Grid item width={"100%"} mb={2}>
              <TableContainer component={Paper} sx={{ width: "100%" }}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600 }}>
                          Description
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600 }}>
                          Payment Method
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600 }}>Qty</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600 }}>
                          Value($)
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600 }}>
                          Amount($)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formattedData?.map((transaction: any, index: any) => {
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="center">
                            {transaction?.description}
                          </TableCell>
                          <TableCell align="center">
                            {transaction?.paymentType}
                          </TableCell>
                          <TableCell align="center">
                            {transaction?.qty ? transaction?.qty : ""}
                          </TableCell>
                          <TableCell align="center">
                            {transaction?.rate ? transaction?.rate : ""}
                          </TableCell>
                          <TableCell align="center">
                            {transaction?.amount ? transaction?.amount : ""}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow
                      key={"rowName"}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600 }}>Total</Typography>
                      </TableCell>
                      <TableCell align="center"></TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600 }}>
                          {isNaN(totalQty) ? "-" : totalQty}
                        </Typography>
                      </TableCell>
                      <TableCell align="center"></TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600 }}>
                          {isNaN(totalAmount) ? "-" : totalAmount}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid
              item
              container
              display={"flex"}
              justifyContent={"space-between"}
            >
              <Grid p={2}>
                {paymentTypesTotal?.map((payTypes: any, index: any) => {
                  return (
                    <Grid
                      display={"flex"}
                      justifyContent={"space-between"}
                      key={index}
                    >
                      <Typography sx={{ fontWeight: 600 }}>
                        {payTypes.type ?? "Total"}:
                      </Typography>
                      <Typography sx={{ fontWeight: 600, ml: "2rem" }}>
                        {isNaN(payTypes.total) ? "-" : "$" + payTypes.total}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
              <Grid p={2} display={"flex"} alignItems={"flex-end"}>
                <Typography sx={{ fontWeight: 600 }}>
                  {new Date().toDateString()}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </div>
      ) : (
        <Loader />
      )}
      <Button
        sx={{
          ...buttonStyles.containedSecondary,
          alignSelf: "end",
          mt: 1,
          mr: 4,
        }}
        onClick={handlePrint}
      >
        Print Report
      </Button>
    </Grid>
  );
};

export default EvoucherSalesReport;
